import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  Input,
  Spacer,
  Switch,
  Text,
  Textarea,
  Tooltip,
  useUpdateEffect,
} from "@chakra-ui/react";

import { themeStore } from "@/stores/theme";
import { getFillTextareaProps } from "@/styles/textArea";

import { genBlock } from "@/utils/bem";
import { useTranslate } from "@/utils/lang";
import { Editor_Words } from "@/utils/lang/editor";

import Icon from "@/components/Icon";
import DraggableImageList from "./ImageList";
import { PostWorkValueError, PostWorkValues } from "./type";
import { validators } from "./utils";

import { ReactComponent as InfoSvg } from "@/assets/icons/info.svg";
import { observer } from "mobx-react-lite";

import "./publish-form.scss";
import classNames from "classnames";
import { PostWorkRequestData, ServerPostItemType } from "@/modules/editor/request";
import { checkAIMeta, useOwnToast } from "@/utils/misc";

const defaultPostWorkValue: PostWorkValues = {
  title: "",
  description: "",
  publicPrompt: true,
  images: [],
};

const block = genBlock("c-publish-form");
const { translate } = useTranslate();

interface IPublishFormProps {
  defaultValues?: PostWorkValues;
  onConfirm?: (serverData: PostWorkRequestData, values: PostWorkValues) => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  submitDisabled?: boolean;
  className?: string;
  loading?: boolean;
  isEdit?: boolean;
  footerClassName?: string;
  bodyClassName?: string;
}
const IPublishForm: React.FC<IPublishFormProps> = ({
  defaultValues = defaultPostWorkValue,
  confirmText = translate(Editor_Words["button_confirm-to-post"]),
  cancelText = translate(Editor_Words["button_cancel"]),
  submitDisabled = false,
  onConfirm,
  onCancel,
  className,
  loading,
  isEdit,
  footerClassName,
  bodyClassName,
}) => {
  const { titleColor, disabledTextColor, fillColor, textSecondaryColor } = themeStore.styles;
  const toast = useOwnToast();

  const [postWorkValues, setPostWorkValues] = useState<PostWorkValues>(defaultValues);
  const { images, title, description, publicPrompt } = postWorkValues;
  const [valueError, setValueError] = useState<PostWorkValueError | null>(null);

  useUpdateEffect(() => {
    setPostWorkValues(defaultValues);
  }, [defaultValues]);

  function updatePostWorkValues<T extends keyof PostWorkValues>(key: T, value: PostWorkValues[T]) {
    setPostWorkValues((values) => ({
      ...values,
      [key]: value,
    }));
  }
  function handleDragEnd(newList: PostWorkValues["images"]) {
    updatePostWorkValues("images", newList);
  }

  function handleDeleteImage(newList: PostWorkValues["images"]) {
    updatePostWorkValues("images", newList);
  }

  function handleConfirmPost() {
    if (loading) return;
    const noEmptyFields: "title"[] = ["title"];
    for (const key of noEmptyFields) {
      if (!postWorkValues[key]) {
        toast({
          status: "warning",
          title: translate(Editor_Words[`validate_no-empty-${key}`]),
        });
        return;
      }
    }
    const fieldsToCheck: ("title" | "description")[] = ["title", "description"];
    for (const key of fieldsToCheck) {
      const validatorResult = validators[key](postWorkValues[key]);
      if (validatorResult !== true) {
        toast({
          status: "error",
          title: validatorResult.msg,
          duration: 6000,
        });
        return;
      }
    }

    const serverData: PostWorkRequestData = {
      title,
      describe: description,
      is_open: publicPrompt ? 1 : 2,
      opus_image: images.map((image) => {
        const itemData: ServerPostItemType = {
          meta_json: image.meta,
          is_ai: checkAIMeta(image.meta) ? 1 : 2,
          image_url: image.src,
        };
        if (isEdit) {
          itemData.image_id = typeof image.id === "string" ? parseInt(image.id) : image.id;
        }
        return itemData;
      }),
    };
    onConfirm?.(serverData, postWorkValues);
  }

  function handleCancel(e: React.MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) {
    e.stopPropagation();
    onCancel?.();
  }

  useEffect(() => {
    if (valueError?.msg) {
      toast({
        status: "error",
        title: valueError.msg,
        isClosable: true,
        position: "top",
        duration: 6000,
      });
    }
  }, [valueError]);

  return (
    <Flex
      h="100%"
      className={classNames(block(), className)}
      flexDirection="column"
      justifyContent="space-between"
    >
      <Box className={classNames(block("content"), bodyClassName)}>
        <Flex gap="18px" alignItems="center" mb="30px">
          <Text className={block("label")} color={titleColor}>
            {translate(Editor_Words["label_title"])}
          </Text>
          <Input
            onChange={(e) => {
              updatePostWorkValues("title", e.target.value);
              setValueError((err) => {
                if (err?.field === "title") {
                  return null;
                }
                return err;
              });
            }}
            value={title}
            w="458px"
            onBlur={(e) => {
              const result = validators.title(e.target.value);
              if (result !== true) setValueError(result);
            }}
            isInvalid={valueError?.field === "title"}
            errorBorderColor="crimson"
            variant="fill"
          />

          <Spacer />

          <Flex alignItems="center" gap="9px">
            <Text className={block("label")} color={titleColor}>
              {translate(Editor_Words["label_prompt-public"])}
            </Text>
            <Tooltip
              bg={fillColor}
              w="350px"
              padding="8px 12px"
              label={
                <Text color={disabledTextColor} fontSize="15px">
                  {translate(Editor_Words["explanation_prompt-public"])}
                </Text>
              }
              placement="top-start"
              offset={[-4, 12]}
              hasArrow
            >
              <div>
                <Icon fontSize="15px" svg={InfoSvg} />
              </div>
            </Tooltip>
          </Flex>
          <Switch
            size="md"
            isChecked={publicPrompt}
            className={block({ e: "switch" })}
            onChange={(e) => {
              updatePostWorkValues("publicPrompt", e.target.checked);
            }}
          />
        </Flex>

        <Flex gap="18px" alignItems="baseline" mb="40px">
          <Text className={block("label")} color={titleColor}>
            {translate(Editor_Words["label_description"])}
          </Text>
          <Textarea
            w="458px"
            value={description}
            onChange={(e) => updatePostWorkValues("description", e.target.value)}
            onBlur={(e) => {
              const result = validators.description(e.target.value);
              if (result !== true) setValueError(result);
            }}
            isInvalid={valueError?.field === "description"}
            errorBorderColor="crimson"
            {...getFillTextareaProps()}
          />
        </Flex>

        <Flex gap="18px" alignItems="start">
          <Text className={block("label")} color={titleColor} whiteSpace="nowrap">
            {translate(Editor_Words["label_art-work"])}
          </Text>
          <Flex flexDirection="column">
            <DraggableImageList
              list={images}
              onDragEnd={handleDragEnd}
              onDelete={handleDeleteImage}
            />
            <Text fontSize="12px" color={textSecondaryColor}>
              {translate(Editor_Words["tip_reorder-on-dragging"])}
            </Text>
          </Flex>
        </Flex>
      </Box>
      <Flex className={classNames(block("footer"), footerClassName)}>
        <Center gap="10px" w="100%">
          <Button
            w="145px"
            onClick={handleConfirmPost}
            isLoading={loading}
            loadingText={confirmText}
            disabled={loading || submitDisabled}
          >
            {confirmText}
          </Button>
          <Button variant="dark.solid" w="145px" onClick={handleCancel}>
            {cancelText}
          </Button>
        </Center>
      </Flex>
    </Flex>
  );
};

export const PublishForm = observer(IPublishForm);
